/**
 * Currency configuration constants
 * Centralized currency settings for consistent display across the application
 */

export const CURRENCY_CONFIG = {
  // Primary currency settings
  CURRENCY_CODE: 'INR',
  CURRENCY_SYMBOL: '₹',
  LOCALE: 'en-IN',
  
  // Formatting options
  MINIMUM_FRACTION_DIGITS: 0,
  MAXIMUM_FRACTION_DIGITS: 2,
  
  // Display options
  SHOW_SYMBOL: true,
  SHOW_CODE: false,
} as const;

/**
 * Format currency amount with consistent settings
 * @param amount - The amount to format (number or string)
 * @param options - Optional formatting overrides
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | string,
  options?: {
    showSymbol?: boolean;
    showCode?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return CURRENCY_CONFIG.SHOW_SYMBOL ? `${CURRENCY_CONFIG.CURRENCY_SYMBOL}0` : '0';
  }

  const formatOptions = {
    style: 'currency' as const,
    currency: CURRENCY_CONFIG.CURRENCY_CODE,
    minimumFractionDigits: options?.minimumFractionDigits ?? CURRENCY_CONFIG.MINIMUM_FRACTION_DIGITS,
    maximumFractionDigits: options?.maximumFractionDigits ?? CURRENCY_CONFIG.MAXIMUM_FRACTION_DIGITS,
  };

  const formatted = new Intl.NumberFormat(CURRENCY_CONFIG.LOCALE, formatOptions).format(numAmount);
  
  // If symbol is disabled, remove it
  if (!(options?.showSymbol ?? CURRENCY_CONFIG.SHOW_SYMBOL)) {
    return formatted.replace(CURRENCY_CONFIG.CURRENCY_SYMBOL, '').trim();
  }
  
  return formatted;
};

/**
 * Get just the currency symbol
 */
export const getCurrencySymbol = (): string => CURRENCY_CONFIG.CURRENCY_SYMBOL;

/**
 * Get the currency code
 */
export const getCurrencyCode = (): string => CURRENCY_CONFIG.CURRENCY_CODE;

/**
 * Format currency for display in tables/lists (compact format)
 */
export const formatCurrencyCompact = (amount: number | string): string => {
  return formatCurrency(amount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Format currency for detailed display (with decimals)
 */
export const formatCurrencyDetailed = (amount: number | string): string => {
  return formatCurrency(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
